import React from 'react';
import { Screen } from '../enums/screen.enum';
import { Trans, useTranslation } from 'react-i18next';
import ContentContainer from '../partials/ContentContainer';
import ActionsContainer from '../partials/ActionsContainer';
import { Button, ButtonGroup, ButtonVariant } from '../components/Button';
import ScreenContainer from '../partials/ScreenContainer';
import { useLocalStorage } from '@uidotdev/usehooks';
import { Constat } from '../models/Constat';
import { ConstatUtilsService } from '../services/constat-utils.service';
import { Vehicle } from '../models/Vehicle';
import VehicleContainer, {
  VehicleContainerVariant,
} from '../partials/VehicleContainer';
import { Form } from '../components/Form';
import { InputField, InputFieldType } from '../components/InputField';
import { VehicleIcon } from '../components/VehicleIcon';

export default function Step1VehicleScreen({
  isActive,
  setScreen,
}: {
  isActive: boolean;
  setScreen: (screen: Screen) => void;
}) {
  const { t } = useTranslation();

  const [constat, setConstat] = useLocalStorage('constat', new Constat());
  const constatUtilsService = new ConstatUtilsService();

  const handleChange = (fieldName: string, value: any) => {
    setConstat((prevConstat) => {
      const newConstat = new Constat(prevConstat);
      if (!newConstat.vehicleA) {
        newConstat.vehicleA = new Vehicle();
      }
      newConstat.vehicleA.setField(fieldName, value);
      return newConstat;
    });
  };

  return !isActive ? (
    <></>
  ) : (
    <ScreenContainer
      title={
        <Trans i18nKey={'screens.step1-vehicle.title'}>
          <strong></strong>
        </Trans>
      }
      timelineStep={1}
      extraCssClasses={['screen-step', 'step1-vehicle']}
    >
      <ContentContainer>
        <h3>{t('screens.step1-vehicle.instructions')}</h3>
        <VehicleContainer variant={VehicleContainerVariant.A}>
          <h2>
            <VehicleIcon shape={constat.vehicleA?.vehicleShape}></VehicleIcon>
            {t('screens.step1-vehicle.second-title')}
          </h2>
        </VehicleContainer>
        <Form
          onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
          }}
        >
          <InputField
            name="vehicleRegistrationNumber"
            id="vehicleRegistrationNumber"
            type={InputFieldType.Text}
            required={constatUtilsService.vehicleRegistrationNumberRequired(
              constat.vehicleA,
            )}
            label={t('vehicle.label.vehicleRegistrationNumber')}
            value={constat.vehicleA?.vehicleRegistrationNumber || ''}
            onChange={(value) =>
              handleChange('vehicleRegistrationNumber', value)
            }
          />
          <InputField
            name="trailerRegistrationNumber"
            id="trailerRegistrationNumber"
            type={InputFieldType.Text}
            required={false}
            label={t('vehicle.label.trailerRegistrationNumber')}
            value={constat.vehicleA?.trailerRegistrationNumber || ''}
            onChange={(value) =>
              handleChange('trailerRegistrationNumber', value)
            }
          />
          <InputField
            name="insuranceCompanyName"
            id="insuranceCompanyName"
            type={InputFieldType.Text}
            required={true}
            label={t('vehicle.label.insuranceCompanyName')}
            value={constat.vehicleA?.insuranceCompanyName || ''}
            onChange={(value) => handleChange('insuranceCompanyName', value)}
          />
          <InputField
            name="insuranceCompanyPolicyNumber"
            id="insuranceCompanyPolicyNumber"
            type={InputFieldType.Text}
            required={true}
            label={t('vehicle.label.insuranceCompanyPolicyNumber')}
            value={constat.vehicleA?.insuranceCompanyPolicyNumber || ''}
            onChange={(value) =>
              handleChange('insuranceCompanyPolicyNumber', value)
            }
          />
        </Form>
      </ContentContainer>

      <ActionsContainer>
        <ButtonGroup>
          <Button
            variant={ButtonVariant.Green}
            isOutlined
            onClick={() => setScreen(Screen.step1Driver)}
          >
            {t('common.previous')}
          </Button>

          <Button
            variant={ButtonVariant.Green}
            isDisabled={
              !constatUtilsService.isVehicleLastDataComplete(
                new Vehicle(constat.vehicleA),
              )
            }
            onClick={() => setScreen(Screen.step2ChoiceType)}
          >
            {t('common.next')}
          </Button>
        </ButtonGroup>
      </ActionsContainer>
    </ScreenContainer>
  );
}
